import { useEffect, useState } from 'react'
import { BsCheck, BsX } from 'react-icons/bs'

interface PasswordProps {
  name: string
  value?: string
  onChange: () => void
  onBlur: (data: any) => void
  checkValidity?: boolean
}

export default function Password({
  name,
  value = '',
  onChange,
  onBlur,
  checkValidity = false,
}: PasswordProps) {
  const [isPasswordVisible, setPasswordVisible] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [hasSpecialCharacters, setHasSpecialCharacters] = useState(false)
  const [hasNumbers, setHasNumbers] = useState(false)
  const [hasLowerCase, setHasLowerCase] = useState(false)
  const [hasUpperCase, setHasUpperCase] = useState(false)
  const [isLongEnough, setIsLongEnough] = useState(false)

  useEffect(() => {
    if (checkValidity) {
      if (value.match(/[!@#$%^&*(),.?":{}|<>]/g)) {
        setHasSpecialCharacters(true)
      } else {
        setHasSpecialCharacters(false)
      }
      if (value.match(/\d+/g)) {
        setHasNumbers(true)
      } else {
        setHasNumbers(false)
      }
      if (value.match(/[a-z]+/g)) {
        setHasLowerCase(true)
      } else {
        setHasLowerCase(false)
      }
      if (value.match(/[A-Z]+/g)) {
        setHasUpperCase(true)
      } else {
        setHasUpperCase(false)
      }
      if (value.length > 11) {
        setIsLongEnough(true)
      } else {
        setIsLongEnough(false)
      }
    }
  }, [value, checkValidity])
  const component = (
    <div className="relative">
      <input
        id={name}
        type={isPasswordVisible ? 'text' : 'password'}
        className="w-full ircam-input text-base outline-none"
        onChange={onChange}
        onBlur={(...rest) => {
          onBlur(...rest)
          setIsFocused(false)
        }}
        value={value || ''}
        onFocus={() => setIsFocused(true)}
      />
      <button
        className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-600"
        type="button"
        onClick={() => setPasswordVisible(!isPasswordVisible)}
      >
        {isPasswordVisible ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        )}
      </button>
    </div>
  )

  if (!checkValidity) {
    return component
  }
  return (
    <div className="relative" style={{ zIndex: 40 }}>
      <div
        className={`${isFocused ? 'visible' : 'sr-only'} absolute z-20 -right-[18rem] -top-[100%] bg-ircamprimary-menu rounded-lg p-4 text-white`}
      >
        Your password should
        <ul>
          <li
            className={`${isLongEnough ? 'text-notification-ok-text' : 'text-notification-ko-text'}`}
          >
            <span className="inline-block w-4">
              {isLongEnough ? (
                <BsCheck className="inline-block" />
              ) : (
                <BsX className="inline-block" />
              )}
            </span>
            Have at least 12 characters
          </li>
          <li
            className={`${hasUpperCase ? 'text-notification-ok-text' : 'text-notification-ko-text'}`}
          >
            <span className="inline-block w-4">
              {hasUpperCase ? (
                <BsCheck className="inline-block" />
              ) : (
                <BsX className="inline-block" />
              )}
            </span>
            Have at least one uppercase letter
          </li>
          <li
            className={`${hasLowerCase ? 'text-notification-ok-text' : 'text-notification-ko-text'}`}
          >
            <span className="inline-block w-4">
              {hasLowerCase ? (
                <BsCheck className="inline-block" />
              ) : (
                <BsX className="inline-block" />
              )}
            </span>
            Have at least one lowercase letter
          </li>
          <li
            className={`${hasNumbers ? 'text-notification-ok-text' : 'text-notification-ko-text'}`}
          >
            <span className="inline-block w-4">
              {hasNumbers ? (
                <BsCheck className="inline-block" />
              ) : (
                <BsX className="inline-block" />
              )}
            </span>
            Have at least one number
          </li>
          <li
            className={`${hasSpecialCharacters ? 'text-notification-ok-text' : 'text-notification-ko-text'}`}
          >
            <span className="inline-block w-4">
              {hasSpecialCharacters ? (
                <BsCheck className="inline-block" />
              ) : (
                <BsX className="inline-block" />
              )}
            </span>
            Have at least one special character
          </li>
        </ul>
      </div>
      {component}
    </div>
  )
}
